import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { inr } from "../lib/api";
import { Check, Clock, MapPin, Truck, ChevronLeft, Printer, Download } from "lucide-react";

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const { user, checking } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get(`/me/orders/${orderId}`);
        setOrder(data);
      } catch {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [user, orderId]);

  if (checking) return <div className="max-w-3xl mx-auto px-6 py-20">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: `/account/orders/${orderId}` }} replace />;
  if (loading) return <div className="max-w-3xl mx-auto px-6 py-20">Loading order...</div>;
  if (notFound || !order) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center">
      <p className="text-welljio-muted mb-6">Order not found.</p>
      <Link to="/account" className="text-welljio-accent hover:underline">Back to account</Link>
    </div>
  );

  const eta = order.estimated_delivery ? new Date(order.estimated_delivery) : null;

  return (
    <div data-testid="order-detail-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/account" data-testid="order-back-link" className="inline-flex items-center text-sm text-welljio-muted hover:text-welljio-ink mb-6">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5}/> Back to orders
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-6 mb-10">
        <div>
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-3">Order tracking</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Order {order.id.slice(-8)}</h1>
          <p className="text-welljio-muted mt-2">Placed {new Date(order.created_at).toLocaleString("en-IN")}</p>
          <div className="flex items-center gap-3 mt-4" data-testid="account-invoice-actions">
            <a
              href={`/invoice/${order.id}?autoprint=1`}
              target="_blank"
              rel="noreferrer"
              data-testid="account-print-invoice"
              className="inline-flex items-center gap-2 rounded-full border border-welljio-ink/15 bg-white px-4 py-2 text-xs font-medium hover:bg-welljio-tint transition-colors"
            >
              <Printer size={14} strokeWidth={2.2} /> Print Invoice
            </a>
            <a
              href={`/invoice/${order.id}?autoprint=1`}
              target="_blank"
              rel="noreferrer"
              data-testid="account-download-invoice"
              className="inline-flex items-center gap-2 rounded-full bg-welljio-accent text-white px-4 py-2 text-xs font-medium hover:opacity-90 transition-opacity"
            >
              <Download size={14} strokeWidth={2.2} /> Download PDF
            </a>
          </div>
        </div>
        {eta && (
          <div className="bg-welljio-highlight rounded-2xl px-5 py-4 max-w-sm">
            <p className="text-xs uppercase tracking-widest text-welljio-accent mb-1 flex items-center gap-2"><Truck className="w-3.5 h-3.5" strokeWidth={1.5}/> Estimated delivery</p>
            <p className="font-heading font-semibold text-welljio-ink">{eta.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
        )}
      </div>

      {/* Tracking timeline */}
      <section data-testid="tracking-timeline" className="bg-white border border-slate-100 rounded-3xl p-8 mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-6">Tracking timeline</h2>
        <div className="space-y-6">
          {(order.timeline || []).map((step, idx) => (
            <div key={step.key} className="flex gap-4" data-testid={`timeline-step-${step.key}`}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  step.completed ? "bg-welljio-green text-white" : "bg-welljio-tint text-welljio-subtle"
                }`}>
                  {step.completed ? <Check className="w-5 h-5" strokeWidth={2} /> : <Clock className="w-4 h-4" strokeWidth={1.5} />}
                </div>
                {idx < (order.timeline.length - 1) && (
                  <div className={`w-0.5 flex-1 ${step.completed ? "bg-welljio-green" : "bg-slate-200"} my-1`} style={{ minHeight: 28 }} />
                )}
              </div>
              <div className="pb-2">
                <p className="font-heading font-semibold text-welljio-ink">{step.label}</p>
                <p className="text-sm text-welljio-muted mt-0.5">{step.description}</p>
                {step.timestamp && (
                  <p className="text-xs text-welljio-subtle mt-1">{new Date(step.timestamp).toLocaleString("en-IN")}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 bg-white border border-slate-100 rounded-3xl p-8">
          <h2 className="font-heading text-xl font-semibold mb-5">Items</h2>
          <div className="space-y-4">
            {order.items?.map((it) => (
              <div key={it.product_id} className="flex gap-4 items-center" data-testid={`order-item-${it.product_id}`}>
                {it.image && <img src={it.image} alt={it.name} className="w-16 h-16 rounded-2xl object-cover bg-welljio-tint" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium line-clamp-1">{it.name}</p>
                  <p className="text-xs text-welljio-muted">Qty {it.quantity} · {inr(it.price)} each</p>
                </div>
                <p className="font-medium">{inr(it.line_total)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 mt-6 pt-4 space-y-1 text-sm">
            <div className="flex justify-between text-welljio-muted"><span>Subtotal</span><span>{inr(order.subtotal)}</span></div>
            <div className="flex justify-between text-welljio-muted"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : inr(order.shipping)}</span></div>
            <div className="flex justify-between font-heading text-lg pt-2"><span>Total paid</span><span>{inr(order.total)}</span></div>
          </div>
        </section>

        <aside className="space-y-4">
          <section className="bg-white border border-slate-100 rounded-3xl p-6">
            <h3 className="font-heading text-sm uppercase tracking-widest text-welljio-subtle mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" strokeWidth={1.5}/> Shipping to</h3>
            <p className="font-medium text-sm">{order.customer?.name}</p>
            <p className="text-sm text-welljio-muted leading-relaxed">
              {order.shipping_address?.line1}<br/>
              {order.shipping_address?.line2 && <>{order.shipping_address.line2}<br/></>}
              {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.pincode}
            </p>
            <p className="text-xs text-welljio-subtle mt-3">{order.customer?.phone}</p>
          </section>
          <section className="bg-white border border-slate-100 rounded-3xl p-6">
            <h3 className="font-heading text-sm uppercase tracking-widest text-welljio-subtle mb-3">Payment</h3>
            <p className="text-sm font-medium capitalize">{order.payment_method}</p>
            <p className="text-xs text-welljio-muted mt-1">Status: {order.status}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}
